import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError } from "../api";
import { useLang } from "../i18n";
import { Button } from "../components/ui/button";

// TEST payment provider panel — simulates the card step WITHOUT any real payment.
// Clearly labeled; both outcomes exercise the real signed-webhook entitlement path.
export default function TestPaymentPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { checkoutId } = useParams();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") || "/panel";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/billing/checkout/${checkoutId}`).then((r) => setData(r.data)).catch((e) => setError(e));
  }, [checkoutId]);

  const pay = async (outcome) => {
    setSending(true);
    try {
      const r = await api.post("/billing/test/complete", { checkout_id: checkoutId, outcome });
      setResult({ outcome, granted: r.data.granted === true });
    } catch (e) {
      setError(e);
    }
    setSending(false);
  };

  const amount = data?.checkout?.amount_grosze;

  return (
    <div data-testid="test-payment-page" className="mx-auto max-w-lg px-4 py-16 sm:px-6">
      <Helmet><title>{`${t("pay.test.title")} — PlanGmina`}</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <p data-testid="test-pay-banner" className="flex items-start gap-2 rounded-lg border-2 border-amber-400 bg-amber-50 px-4 py-3 text-xs font-bold text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          {t("pay.test.banner")}
        </p>
        {error && <p data-testid="test-pay-error" className="mt-4 text-sm text-rose-700">{formatApiError(error)}</p>}
        {data && !result && (
          <>
            <h1 className="mt-6 font-heading text-xl font-extrabold text-slate-900">{t("pay.test.title")}</h1>
            <p className="mt-2 text-sm text-slate-600">
              {data.kind === "report" ? t("checkout.item.report") : data.checkout.plan_code}
              {" — "}
              <strong data-testid="test-pay-amount">
                {amount != null ? `${(amount / 100).toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł` : "—"}
              </strong>
            </p>
            <div className="mt-6 space-y-2">
              <Button data-testid="test-pay-success" onClick={() => pay("success")} disabled={sending}
                className="h-11 w-full bg-emerald-600 text-white hover:bg-emerald-700">
                {t("pay.test.success")}
              </Button>
              <Button data-testid="test-pay-failure" onClick={() => pay("failure")} disabled={sending} variant="outline"
                className="h-11 w-full">
                {t("pay.test.failure")}
              </Button>
            </div>
          </>
        )}
        {result && (
          <div className="mt-6 text-center" data-testid="test-pay-result">
            {result.outcome === "success" && result.granted ? (
              <>
                <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
                <p className="mt-3 text-sm font-semibold text-emerald-800">{t("pay.test.success_msg")}</p>
              </>
            ) : (
              <>
                <XCircle size={40} className="mx-auto text-rose-600" />
                <p className="mt-3 text-sm font-semibold text-rose-800">{t("pay.test.failure_msg")}</p>
              </>
            )}
            <Button data-testid="test-pay-return" className="mt-6 bg-slate-900 text-white hover:bg-slate-800"
              onClick={() => navigate(returnTo)}>
              {t("pay.test.back")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
